import { memo, useCallback } from "react";
import { TableCell } from "@mui/material";

interface EditableNumberCellProps {
  value: string;
  field: string; // Generic field name instead of keyof specific type
  identifier: string | number; // Generic identifier that can be string or number
  index: number;
  maxWidth?: number | string;
  color: string;
  hasError: boolean;
  onBlur: (
    e: React.FocusEvent<HTMLTableCellElement>,
    field: string,
    identifier: string | number,
    value: number,
    index: number,
  ) => void;
  onInput: (e: any, field: string, index: number) => void;
  onKeyDown: (e: any, field: string, index: number) => void;
  onFocus: (e: React.FocusEvent<HTMLDivElement>) => void;
  parseNumber: (value: string) => number;
}

const EditableNumberCell = memo<EditableNumberCellProps>(
  ({
    value,
    field,
    identifier,
    index,
    maxWidth,
    color,
    hasError,
    onBlur,
    onInput,
    onKeyDown,
    onFocus,
    parseNumber,
  }) => {
    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTableCellElement>) => {
        onBlur(e, field, identifier, parseNumber(e.target.innerText), index);
      },
      [onBlur, field, identifier, parseNumber, index],
    );

    const handleInput = useCallback(
      (e: any) => {
        onInput(e, field, index);
      },
      [onInput, field, index],
    );

    const handleKeyDown = useCallback(
      (e: any) => {
        onKeyDown(e, field, index);
      },
      [onKeyDown, field, index],
    );

    // Restrict to a digit-only string so dangerouslySetInnerHTML cannot inject markup
    // (user input is already filtered to digits, and API values are numeric).
    const safeHtml = value == null || value === "" ? "" : String(value).replace(/[^0-9.]/g, "");

    return (
      <TableCell
        sx={{
          backgroundColor: "var(--white)",
          border: "2px solid var(--silver-150)",
          borderColor: hasError ? "var(--red-800)" : "var(--silver-150)",
          fontWeight: "regular",
          textAlign: "center",
          color,
          maxWidth,
          width: maxWidth,
          // Fix height and line-height issues for stable cursor
          height: "48px",
          minHeight: "48px",
          lineHeight: "48px", // Match height for consistent vertical centering
          padding: "0 8px", // Only horizontal padding
          // Stable layout properties
          whiteSpace: "nowrap",
          overflow: "hidden",
          outline: "none",
          boxSizing: "border-box",
          verticalAlign: "baseline", // Consistent baseline alignment
          // Override inherited styles that cause jumping
          py: "0 !important", // Force no vertical padding from parent
          // Contenteditable specific fixes
          "&[contenteditable='true']": {
            outline: "none",
            userSelect: "text",
            cursor: "text",
            lineHeight: "48px", // Keep consistent line height
            // Fix for empty content cursor position
            "&:empty": {
              lineHeight: "48px",
              minHeight: "48px",
            },
          },
          // Focus state with border highlight
          "&:focus": {
            outline: "none",
            lineHeight: "48px",
            borderColor: hasError ? "var(--red-800)" : "initial",
          },
          fontSize: { xs: "16px", md: "18px", xl: "20px" },
        }}
        onFocus={onFocus}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        // Use dangerouslySetInnerHTML so React does NOT manage inner text nodes via the
        // children reconciliation path. With `children={value}` React tracks the rendered
        // text node, and when the user clears the cell directly, React later fails on
        // `removeChild` because the node it expected to remove is no longer in the DOM.
        // `safeHtml` is recomputed every render from `value`, so external state changes
        // (re-fetched values) DO update the cell; during typing the state-driven `safeHtml`
        // stays stable and the user's input is preserved.
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  },
);

EditableNumberCell.displayName = "EditableNumberCell";

export default EditableNumberCell;
